// ============================================================================
// COMPANY RESULTS COMPONENT
// Created by Salvador Janeiro
// ============================================================================

import React from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip
} from 'recharts';
import {
  Globe2, Users, Building2, Award, TrendingUp, Target,
  Download, AlertCircle, CheckCircle, Info
} from 'lucide-react';
import { getCompanyLevelEmoji } from './calculations';

const CompanyResults = ({ results, onReset }) => {
  const { level, levelName, levelColor, levelDescription, overallScore, scores, answers } = results;

  // Prepare radar chart data
  const radarData = [
    { subject: 'Environmental', value: scores.environmental, fullMark: 100 },
    { subject: 'Social', value: scores.social, fullMark: 100 },
    { subject: 'Governance', value: scores.governance, fullMark: 100 },
  ];

  // Generate detailed feedback
  const generateFeedback = () => {
    const feedback = {
      environmental: [],
      social: [],
      governance: [],
    };

    // Environmental feedback
    if (answers.carbonMeasurement === 'no') {
      feedback.environmental.push({
        type: 'gap',
        text: "You haven't started tracking carbon emissions yet. This is a critical first step - companies that measure their footprint are 40% more likely to reduce it."
      });
    }
    if (answers.renewableEnergy === '0' || answers.renewableEnergy === '1-20') {
      feedback.environmental.push({
        type: 'gap',
        text: "Your renewable energy usage is low. Transitioning to renewable energy can reduce operational emissions by up to 80%."
      });
    }
    if (answers.emissionTargets === 'no') {
      feedback.environmental.push({
        type: 'gap',
        text: "Setting emission reduction targets is essential. Public targets drive accountability and show stakeholders you're committed to climate action."
      });
    }
    if (answers.wasteManagement === 'none' || answers.wasteManagement === 'basic') {
      feedback.environmental.push({
        type: 'improvement',
        text: "Develop a comprehensive waste management strategy including recycling, composting, and waste reduction goals."
      });
    }

    // Positive environmental actions
    if (answers.carbonMeasurement === 'verified') {
      feedback.environmental.push({
        type: 'strength',
        text: "Excellent! Third-party verified carbon accounting demonstrates transparency and credibility."
      });
    }
    if (answers.emissionTargets === 'netZero') {
      feedback.environmental.push({
        type: 'strength',
        text: "Your net-zero commitment positions you as a climate leader. Ensure you have a clear roadmap to achieve this goal."
      });
    }
    if (answers.renewableEnergy === '100') {
      feedback.environmental.push({
        type: 'strength',
        text: "100% renewable energy is outstanding! This significantly reduces your operational carbon footprint."
      });
    }

    // Social feedback
    if (answers.laborPractices === 'no' || answers.laborPractices === 'progress') {
      feedback.social.push({
        type: 'gap',
        text: "Strengthen your labor practices policies. Fair wages, safe working conditions, and supply chain audits are increasingly expected by stakeholders."
      });
    }
    if (answers.diversity === 'none' || answers.diversity === 'basic') {
      feedback.social.push({
        type: 'improvement',
        text: "Develop formal D&I policies with measurable goals. Companies with strong D&I programs show better innovation and financial performance."
      });
    }
    if (answers.communityEngagement === 'no') {
      feedback.social.push({
        type: 'gap',
        text: "Community engagement builds social license and strengthens your reputation. Consider establishing a formal community program."
      });
    }

    // Positive social actions
    if (answers.laborPractices === 'audited') {
      feedback.social.push({
        type: 'strength',
        text: "Your supply chain labor audits demonstrate commitment to human rights and responsible business practices."
      });
    }
    if (answers.diversity === 'core') {
      feedback.social.push({
        type: 'strength',
        text: "Making D&I a core value with measurable outcomes is excellent. This creates a more equitable and innovative workplace."
      });
    }
    if (answers.employeeWellbeing === 'robust') {
      feedback.social.push({
        type: 'strength',
        text: "Robust wellness programs improve employee satisfaction, retention, and productivity."
      });
    }

    // Governance feedback
    if (answers.strategyIntegration === 'no' || answers.strategyIntegration === 'exploring') {
      feedback.governance.push({
        type: 'gap',
        text: "Integrate sustainability into your core business strategy. When sustainability drives decisions, companies see better long-term value creation."
      });
    }
    if (answers.esgReporting === 'no') {
      feedback.governance.push({
        type: 'gap',
        text: "Start publishing an ESG report. 90% of S&P 500 companies now report on sustainability - it's becoming the standard for transparency."
      });
    }
    if (!answers.sdgAlignment || (Array.isArray(answers.sdgAlignment) && answers.sdgAlignment.includes('none'))) {
      feedback.governance.push({
        type: 'improvement',
        text: "Align your activities with relevant SDGs. This provides a framework for impact and helps communicate your contributions to global goals."
      });
    }
    if (answers.governanceStructure === 'no') {
      feedback.governance.push({
        type: 'gap',
        text: "Establish dedicated sustainability leadership. Companies with board-level oversight on ESG issues outperform peers financially."
      });
    }

    // Positive governance actions
    if (answers.esgReporting === 'standards') {
      feedback.governance.push({
        type: 'strength',
        text: "Following GRI or SASB standards shows commitment to credible, comparable reporting. This builds trust with investors and stakeholders."
      });
    }
    if (answers.governanceStructure === 'board') {
      feedback.governance.push({
        type: 'strength',
        text: "Board-level oversight ensures sustainability is prioritized at the highest level of decision-making."
      });
    }
    if (answers.transparency === 'completely') {
      feedback.governance.push({
        type: 'strength',
        text: "Complete transparency with full disclosure builds strong stakeholder trust and accountability."
      });
    }

    return feedback;
  };

  const feedback = generateFeedback();

  // Generate recommendations
  const generateRecommendations = () => {
    const recs = [];

    if (level < 3) {
      recs.push({
        priority: 'High',
        title: 'Start Measuring Key Impacts',
        description: 'Implement systems to measure carbon footprint, water usage, and waste. Use frameworks like the GHG Protocol.',
        impact: 'Foundation for all improvement efforts'
      });

      recs.push({
        priority: 'High',
        title: 'Set Clear Targets',
        description: 'Establish reduction targets (e.g., 30% emission cut by 2030) and publish them publicly.',
        impact: 'Drives accountability and stakeholder confidence'
      });
    }

    if (scores.environmental < 60) {
      recs.push({
        priority: 'High',
        title: 'Increase Renewable Energy',
        description: 'Purchase green power or install on-site renewable generation. Many utilities offer renewable energy programs.',
        impact: 'Can reduce operational emissions by 80%'
      });

      recs.push({
        priority: 'Medium',
        title: 'Implement Waste Reduction Strategy',
        description: 'Set zero-waste goals, increase recycling rates, and work with suppliers on packaging reduction.',
        impact: 'Reduces costs and environmental footprint'
      });
    }

    if (scores.social < 60) {
      recs.push({
        priority: 'Medium',
        title: 'Strengthen Labor Standards',
        description: 'Implement fair labor policies and extend them through your supply chain with regular audits.',
        impact: 'Reduces reputational risk and improves retention'
      });

      recs.push({
        priority: 'Medium',
        title: 'Enhance D&I Programs',
        description: 'Set diversity hiring goals, provide inclusive training, and measure outcomes quarterly.',
        impact: 'Improves innovation and financial performance'
      });
    }

    if (scores.governance < 60) {
      recs.push({
        priority: 'High',
        title: 'Begin ESG Reporting',
        description: 'Publish an annual sustainability report following GRI or SASB standards. Start simple and build over time.',
        impact: 'Essential for investor confidence and transparency'
      });

      recs.push({
        priority: 'High',
        title: 'Establish Sustainability Governance',
        description: 'Appoint a sustainability officer or form a committee with board oversight.',
        impact: 'Ensures accountability at leadership level'
      });

      recs.push({
        priority: 'Medium',
        title: 'Align with SDGs',
        description: 'Map your activities to relevant Sustainable Development Goals and report on contributions.',
        impact: 'Provides framework for global impact'
      });
    }

    if (level >= 3) {
      recs.push({
        priority: 'Low',
        title: 'Pursue Advanced Certifications',
        description: 'Consider B-Corp certification, CDP disclosure, or industry-specific sustainability standards.',
        impact: 'Validates leadership and differentiates from competitors'
      });

      recs.push({
        priority: 'Low',
        title: 'Engage Supply Chain',
        description: 'Work with suppliers to reduce their footprint and ensure they meet your sustainability standards.',
        impact: 'Scope 3 emissions often represent 80%+ of total footprint'
      });
    }

    return recs.sort((a, b) => {
      const priorityOrder = { High: 0, Medium: 1, Low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  };

  const recommendations = generateRecommendations();

  // Export functionality
  const exportReport = () => {
    const sdgList = Array.isArray(answers.sdgAlignment) 
      ? answers.sdgAlignment.filter(s => s !== 'none').join(', ')
      : 'None specified';

    const report = `
COMPANY SUSTAINABILITY ASSESSMENT REPORT
Generated: ${new Date().toLocaleDateString()}

OVERALL SUSTAINABILITY LEVEL: ${level} - ${levelName}
Overall Score: ${overallScore}/100

LEVEL DESCRIPTION:
${levelDescription}

ESG SCORES:
- Environmental: ${scores.environmental}/100
- Social: ${scores.social}/100
- Governance: ${scores.governance}/100

SDG ALIGNMENT:
${sdgList}

DETAILED FEEDBACK:

ENVIRONMENTAL:
${feedback.environmental.map(f => `[${f.type.toUpperCase()}] ${f.text}`).join('\n')}

SOCIAL:
${feedback.social.map(f => `[${f.type.toUpperCase()}] ${f.text}`).join('\n')}

GOVERNANCE:
${feedback.governance.map(f => `[${f.type.toUpperCase()}] ${f.text}`).join('\n')}

PRIORITY RECOMMENDATIONS:

${recommendations.map(rec => `
[${rec.priority} Priority] ${rec.title}
${rec.description}
Expected Impact: ${rec.impact}
`).join('\n')}

NEXT STEPS:
1. Review this assessment with leadership team
2. Prioritize recommendations based on resources and impact
3. Set 12-month goals for improvement
4. Schedule reassessment in 12 months to track progress

---
Created by Salvador Janeiro
Sustainability Self-Assessment Platform

Note: This is a self-assessment tool. For comprehensive ESG audits and strategy 
development, consider consulting with a sustainability professional.
    `.trim();

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `company-sustainability-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Company Sustainability Assessment</h1>
          <p className="text-gray-600">Your ESG performance analysis and improvement roadmap</p>
        </div>

        {/* Level Banner */}
        <div 
          className="rounded-2xl shadow-2xl p-8 mb-8 text-white"
          style={{ background: `linear-gradient(135deg, ${levelColor} 0%, ${levelColor}dd 100%)` }}
        >
          <div className="text-center">
            <div className="text-6xl mb-4">{getCompanyLevelEmoji(level)}</div>
            <h2 className="text-4xl font-bold mb-3">Level {level}: {levelName}</h2>
            <p className="text-xl opacity-90 mb-4">{levelDescription}</p>
            <div className="text-3xl font-bold">Overall Score: {overallScore}/100</div>
          </div>
        </div>

        {/* ESG Score Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Environmental */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Environmental</h3>
              <Globe2 className="text-green-600" size={32} />
            </div>
            <div className="text-5xl font-bold text-gray-800 mb-2">{scores.environmental}</div>
            <div className="text-sm text-gray-600 mb-4">out of 100</div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-green-600 h-3 rounded-full transition-all"
                style={{ width: `${scores.environmental}%` }}
              />
            </div>
          </div>

          {/* Social */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Social</h3>
              <Users className="text-blue-600" size={32} />
            </div>
            <div className="text-5xl font-bold text-gray-800 mb-2">{scores.social}</div>
            <div className="text-sm text-gray-600 mb-4">out of 100</div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all"
                style={{ width: `${scores.social}%` }}
              />
            </div>
          </div>

          {/* Governance */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Governance</h3>
              <Building2 className="text-purple-600" size={32} />
            </div>
            <div className="text-5xl font-bold text-gray-800 mb-2">{scores.governance}</div>
            <div className="text-sm text-gray-600 mb-4">out of 100</div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-purple-600 h-3 rounded-full transition-all"
                style={{ width: `${scores.governance}%` }}
              />
            </div>
          </div>
        </div>

        {/* Radar Chart */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">ESG Performance Profile</h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} />
              <Radar 
                name="Your Score" 
                dataKey="value" 
                stroke="#2A9D8F" 
                fill="#2A9D8F" 
                fillOpacity={0.6} 
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Detailed Feedback */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Award className="mr-3 text-blue-600" size={28} />
            Detailed Analysis
          </h3>

          {/* Environmental */}
          <div className="mb-8">
            <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Globe2 className="mr-2 text-green-600" size={24} />
              Environmental Performance
            </h4>
            <div className="space-y-3">
              {feedback.environmental.length > 0 ? feedback.environmental.map((item, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  item.type === 'strength' ? 'bg-green-50 border-green-500' :
                  item.type === 'gap' ? 'bg-red-50 border-red-500' :
                  'bg-yellow-50 border-yellow-500'
                }`}>
                  <div className="flex items-start">
                    {item.type === 'strength' && <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    {item.type === 'gap' && <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    {item.type === 'improvement' && <Info className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                </div>
              )) : (
                <p className="text-gray-500 italic">No specific feedback for environmental practices.</p>
              )}
            </div>
          </div>

          {/* Social */}
          <div className="mb-8">
            <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Users className="mr-2 text-blue-600" size={24} />
              Social Performance
            </h4>
            <div className="space-y-3">
              {feedback.social.length > 0 ? feedback.social.map((item, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  item.type === 'strength' ? 'bg-green-50 border-green-500' :
                  item.type === 'gap' ? 'bg-red-50 border-red-500' :
                  'bg-yellow-50 border-yellow-500'
                }`}>
                  <div className="flex items-start">
                    {item.type === 'strength' && <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    {item.type === 'gap' && <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    {item.type === 'improvement' && <Info className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                </div>
              )) : (
                <p className="text-gray-500 italic">No specific feedback for social practices.</p>
              )}
            </div>
          </div>

          {/* Governance */}
          <div>
            <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Building2 className="mr-2 text-purple-600" size={24} />
              Governance Performance
            </h4>
            <div className="space-y-3">
              {feedback.governance.length > 0 ? feedback.governance.map((item, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  item.type === 'strength' ? 'bg-green-50 border-green-500' :
                  item.type === 'gap' ? 'bg-red-50 border-red-500' :
                  'bg-yellow-50 border-yellow-500'
                }`}>
                  <div className="flex items-start">
                    {item.type === 'strength' && <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    {item.type === 'gap' && <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    {item.type === 'improvement' && <Info className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                </div>
              )) : (
                <p className="text-gray-500 italic">No specific feedback for governance practices.</p>
              )}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-lg p-8 mb-8 border-2 border-blue-200">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Target className="mr-3 text-blue-600" size={28} />
            Priority Recommendations
          </h3>
          <div className="space-y-4">
            {recommendations.map((rec, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-bold text-lg text-gray-800">{rec.title}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    rec.priority === 'High' ? 'bg-red-100 text-red-700' :
                    rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {rec.priority} Priority
                  </span>
                </div>
                <p className="text-gray-600 mb-3">{rec.description}</p>
                <div className="flex items-start text-sm text-blue-700 bg-blue-50 p-3 rounded">
                  <TrendingUp size={16} className="mr-2 flex-shrink-0 mt-0.5" />
                  <span><strong>Expected Impact:</strong> {rec.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Industry Context */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Industry Context & Benchmarks</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-2">ESG Reporting</h4>
              <p className="text-gray-600 text-sm mb-2">
                90% of S&P 500 companies now publish ESG reports. Transparency is becoming the standard 
                for maintaining investor confidence and stakeholder trust.
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-2">SDG Alignment</h4>
              <p className="text-gray-600 text-sm mb-2">
                53% of income for top companies is linked to SDG-related operations. Aligning with 
                global goals provides framework and demonstrates impact.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-2">Investor Expectations</h4>
              <p className="text-gray-600 text-sm mb-2">
                89% of investors now consider ESG factors in investment decisions. Strong ESG 
                performance is linked to better financial returns.
              </p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-2">Net-Zero Commitments</h4>
              <p className="text-gray-600 text-sm mb-2">
                Leading companies are setting science-based targets and committing to net-zero emissions. 
                This demonstrates climate leadership and future-proofs the business.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        {level < 3 && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <AlertCircle size={32} className="flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Ready to Elevate Your Sustainability Performance?</h3>
                <p className="text-lg opacity-90 mb-4">
                  Moving from Level {level} to Level 3 (Advanced) requires strategic planning and 
                  implementation expertise. Consider working with a sustainability professional to 
                  develop a comprehensive ESG strategy tailored to your organization.
                </p>
                <div className="bg-white text-gray-800 p-6 rounded-lg">
                  <p className="font-semibold text-xl mb-2">Contact Salvador Janeiro</p>
                  <p className="text-sm text-gray-600 mb-3">AI & Sustainability Expert</p>
                  <ul className="text-sm space-y-2">
                    </ul>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <a
                      href="mailto:salvadorjaneiro02@gmail.com"
                      className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-all"
                    >
                      📧 Email Me
                    </a>
                    <a
                      href="https://www.linkedin.com/in/salvador-janeiro-419010296"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all"
                    >
                      💼 LinkedIn
                    </a>
                  </div>
                  <div>
                    <li className="flex items-start">
                      <CheckCircle size={16} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Comprehensive ESG strategy development</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={16} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Carbon accounting and reduction planning</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={16} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Sustainability reporting (GRI, SASB, TCFD)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={16} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Stakeholder engagement and materiality assessments</span>
                    </li>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {level >= 3 && (
          <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl shadow-lg p-8 mb-8">
            <div className="text-center">
              <div className="text-5xl mb-4">🌟</div>
              <h3 className="text-2xl font-bold mb-2">Excellent Progress!</h3>
              <p className="text-lg opacity-90 mb-4">
                Your company is demonstrating strong sustainability leadership. To maintain this momentum 
                and continue improving, consider periodic reassessments and external verification of your efforts.
              </p>
              <p className="text-sm opacity-80">
                For advanced support, impact measurement, or third-party verification, expert guidance 
                can help you maximize your sustainability impact and stakeholder value.
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
          <button
            onClick={exportReport}
            className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:scale-105"
          >
            <Download size={20} />
            <span>Download Full Report</span>
          </button>
          
          <button
            onClick={onReset}
            className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-purple-700 transition-all shadow-lg hover:scale-105"
          >
            <Building2 size={20} />
            <span>Take Another Assessment</span>
          </button>
        </div>

        {/* Methodology */}
        <div className="bg-gray-50 rounded-xl p-6 text-sm text-gray-600">
          <h4 className="font-semibold text-gray-800 mb-2">Assessment Methodology</h4>
          <p className="mb-2">
            This assessment evaluates your company's sustainability practices across Environmental, Social, 
            and Governance (ESG) dimensions. Questions are based on internationally recognized frameworks 
            including GRI, SASB, and the UN Sustainable Development Goals.
          </p>
          <p className="mb-2">
            Scoring is weighted based on best practices and regulatory trends. Level classifications are:
            Level 1 (Beginner, 0-29%), Level 2 (Intermediate, 30-59%), Level 3 (Advanced, 60-84%), 
            Level 4 (Leader, 85-100%).
          </p>
          <p>
            This is a self-assessment tool designed for awareness and initial benchmarking. For comprehensive 
            ESG audits, materiality assessments, and strategic planning, consult with sustainability professionals.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          <p>Created by Salvador Janeiro</p>
        </div>
      </div>
    </div>
  );
};

export default CompanyResults;
